import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

// ---------------------------------------------------------------------------
// makeIdempotentResponse: returns a Response-shaped object whose body can be
// read AS MANY TIMES AS supabase-js needs — fixing the iOS Safari /
// mobile in-app browser error "body stream already read" / "Body is
// disturbed or locked" that occurs when supabase-js internally clones a
// response and reads .json() on both the clone and the original (typical
// of the 400 / refresh-token error paths).
// ---------------------------------------------------------------------------
function makeIdempotentResponse(rawResponse, buf) {
  const decoder = new TextDecoder("utf-8");
  let textCache = null;
  const getText = () => {
    if (textCache === null) {
      try { textCache = decoder.decode(buf); } catch (_) { textCache = ""; }
    }
    return textCache;
  };
  let jsonCache;
  let jsonParsed = false;
  const getJson = () => {
    if (!jsonParsed) {
      const t = getText();
      try { jsonCache = t ? JSON.parse(t) : null; }
      catch (e) { jsonParsed = true; throw new SyntaxError("Unexpected token in JSON"); }
      jsonParsed = true;
    }
    return jsonCache;
  };

  // Build a brand-new Response from the buffered bytes and override the
  // read methods so they always resolve from cache.
  const response = new Response(buf.slice(0), {
    status: rawResponse.status,
    statusText: rawResponse.statusText,
    headers: rawResponse.headers,
  });
  response.text = async () => getText();
  response.json = async () => getJson();
  response.arrayBuffer = async () => buf.slice(0);
  response.blob = async () => new Blob([buf]);
  // .clone() returns another idempotent wrapper sharing the same buffer
  response.clone = () => makeIdempotentResponse(rawResponse, buf);
  return response;
}

// safeFetch: pre-buffers the body and returns an idempotent Response.
// Used as the explicit fetch passed to supabase-js (most reliable —
// bypasses any cross-fetch reference cached at module load time).
const safeFetch = async (input, init) => {
  if (typeof window !== "undefined") {
    window.__cec_safefetch_calls__ = (window.__cec_safefetch_calls__ || 0) + 1;
  }
  const fetchFn = (typeof window !== "undefined" && window.fetch?.__cec_original_fetch__) || globalThis.fetch;
  const r = await fetchFn(input, init);
  let buf;
  try { buf = await r.arrayBuffer(); } catch (_) { return r; }
  return makeIdempotentResponse(r, buf);
};

// ---------------------------------------------------------------------------
// Also monkey-patch window.fetch for ANY direct fetch call (defense in depth).
// ---------------------------------------------------------------------------
if (typeof window !== "undefined" && !window.__cec_fetch_patched__ && SUPABASE_URL) {
  const supabaseHost = (() => {
    try { return new URL(SUPABASE_URL).host; } catch (_) { return ""; }
  })();
  const originalFetch = window.fetch.bind(window);

  const patchedFetch = async (input, init) => {
    let url = "";
    try {
      url = typeof input === "string" ? input : (input?.url || "");
    } catch (_) { url = ""; }
    if (!supabaseHost || !url.includes(supabaseHost)) {
      return originalFetch(input, init);
    }
    const r = await originalFetch(input, init);
    let buf;
    try { buf = await r.arrayBuffer(); } catch (_) { return r; }
    return makeIdempotentResponse(r, buf);
  };
  // Expose the original for safeFetch to use, so we don't recurse through the patch.
  patchedFetch.__cec_original_fetch__ = originalFetch;
  window.fetch = patchedFetch;
  window.__cec_fetch_patched__ = true;
}

function getClient() {
  if (!isSupabaseConfigured) return null;
  if (typeof window === "undefined") {
    return createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }
  if (!window.__cec_supabase__) {
    window.__cec_supabase__ = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: "implicit",
        storageKey: "cec-supabase-auth-v3",
      },
      global: {
        // Explicit fetch — bypasses any cross-fetch reference that
        // supabase-js may have cached before our window.fetch patch.
        fetch: safeFetch,
      },
    });
  }
  return window.__cec_supabase__;
}

export const supabase = getClient();
